import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { DialogService, FeedbackService } from '@app/core/services';
import { TableConfig } from '@app/interfaces';
import { MaterialModule } from '@app/shared';
import { TableComponent, TitleButton, TitleButtonsComponent } from '@app/shared/components';
import { FormMode } from '@app/shared/forms/components';
import { AccountService, BankService } from '@app/shared/services';
import { BankBaseTabPageComponent } from '../bank-base-tab-page.component';
import { IBankCashAccount, ICashAccountSelection } from './bank-cash-accounts.models';
import { BankDetailTabs } from '../bank-detail-tabs.constant';

@Component({
	selector: 'bank-cash-accounts',
	standalone: true,
	imports: [MaterialModule, NgIf, TableComponent, TitleButtonsComponent],
	templateUrl: './bank-cash-accounts.component.html',
	styleUrl: './bank-cash-accounts.component.less',
})
export class BankCashAccountsComponent extends BankBaseTabPageComponent implements OnInit {
	@Input() bankId: number;
	@Output() currentBalanceChange: EventEmitter<number> = new EventEmitter<number>();

	@ViewChild('tblSelectAccountDisplay') tblSelectAccountDisplay: TemplateRef<{ row: ICashAccountSelection }>;

	cashAccounts: IBankCashAccount[] = [];
	private revertCashAccounts: string;

	addCashAccountsForm = new UntypedFormGroup({});
	addCashAccountsOptions: FormlyFormOptions = {
		formState: {
			create: false,
			mode: FormMode.Edit,
			readOnly: false,
		},
	};

	private allSelectableCashAccounts: ICashAccountSelection[] = [];
	selectCashAccounts: ICashAccountSelection[];

	titleButtons: TitleButton[] = this.getTitleButtons();
	tableConfig: TableConfig<IBankCashAccount> = this.getCashAccountListConfig();

	constructor(
		private readonly bankService: BankService,
		private readonly accountService: AccountService,
		private readonly dialogService: DialogService,
		private readonly feedbackService: FeedbackService,
	) {
		super();
	}

	ngOnInit(): void {
		this.bankService.getBankCashAccounts(this.bankId).subscribe((accounts) => {
			this.cashAccounts = accounts;
			this.revertCashAccounts = JSON.stringify(accounts);
		});
	}

	onChanges(): void {
		this.tabChange.emit({
			tabName: BankDetailTabs.CashAccounts,
			cancelChanges: () => this.cancelChanges(),
			saveChanges: () => this.saveChanges(),
			isValid: () => true,
		});
	}

	clearChanges(): void {
		this.tabChange.emit(null);
	}

	saveChanges(): Observable<boolean> {
		return this.bankService.updateBankCashAccounts(this.bankId, this.cashAccounts).pipe(
			map((currentBalance: number) => {
				this.revertCashAccounts = JSON.stringify(this.cashAccounts);
				this.currentBalanceChange.emit(currentBalance);
				this.allSelectableCashAccounts = []; // Clear the cached selectable accounts to force a reload
				this.clearChanges();
				return true;
			}),
			catchError(() => of(false)),
		);
	}

	cancelChanges(): void {
		this.cashAccounts = JSON.parse(this.revertCashAccounts);
		this.clearChanges();
	}

	deleteCashAccount(cashAccount: IBankCashAccount): any {
		this.cashAccounts = this.cashAccounts.filter((account) => account.AccountId !== cashAccount.AccountId);
		this.onChanges();
	}

	addCashAccounts(): void {
		const load$ = this.allSelectableCashAccounts.length ? of(null) : this.loadAllCashAccounts();
		load$.subscribe(() => {
			this.mapSelectCashAccounts();
			this.showAddCashAccountsDialog();
		});
	}

	private loadAllCashAccounts(): Observable<void> {
		return this.accountService.getCashAccountsList().pipe(
			map((accounts) => {
				this.allSelectableCashAccounts = accounts.map((account) => ({
					...account,
					AccountId: account.Id,
					Selected: false,
					Warning:
						account.BankId && account.BankId !== this.bankId
							? `This account is already associated with ${account.Bank.ShortName} - ${account.Bank.BankName}.`
							: '',
				}));
			}),
		);
	}

	private mapSelectCashAccounts(): void {
		const selectedAccountIds = this.cashAccounts.map((account) => account.AccountId);
		this.selectCashAccounts = this.allSelectableCashAccounts.map((account) => ({
			...account,
			Selected: selectedAccountIds.includes(account.AccountId),
		}));
	}

	private showAddCashAccountsDialog(): void {
		this.feedbackService.clearAll();
		this.dialogService.open({
			title: 'Select Cash Accounts to link to this Bank',
			fields: this.getSelectCashAccountsFields(),
			model: this.selectCashAccounts,
			form: this.addCashAccountsForm,
			options: this.addCashAccountsOptions,
			minWidth: '75vw',
			okText: 'Continue',
		})
		.subscribe((result) => {
			if (result) {
				const selectedAccounts = (result as unknown as ICashAccountSelection[]).filter((account) => account.Selected);
				this.cashAccounts = selectedAccounts.map((account) => ({ ...account }));
				this.onChanges();
			}
		});
	}

	private getTitleButtons(): TitleButton[] {
		return [
			{
				label: 'Add/Remove Cash Accounts',
				onClick: () => this.addCashAccounts(),
				hidden: () => this.readOnly,
				color: 'primary',
				cssClass: 'linq-button linq-button--primary',
			},
		];
	}

	private getCashAccountListConfig(): TableConfig<IBankCashAccount> {
		return {
			...this.tableConfig,
			columns: [
				{
					header: 'Account Number',
					key: 'AccountNumber',
				},
				{
					header: 'Description',
					key: 'Description',
				},
			],
			defaultSortField: 'AccountNumber',
			defaultSortDirection: 'asc',
			excludePaginator: true,
			showRowSelect: false,
			showRowActions: false,
			selectedId: null,
			onRowClick: (row: IBankCashAccount) => (this.tableConfig.selectedId = row.AccountId),
			editableRows: true,
			editActions: {
				allowUpdate: () => false,
				allowDelete: () => !this.readOnly,
				delete: (row: IBankCashAccount) => this.deleteCashAccount(row),
			},
		};
	}

	private getSelectCashAccountsFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroup: [
					{
						type: FORMLY_FIELD_TYPES.formlyTable,
						props: {
							noDataText: 'No results found.',
							data: [],
							activeFilter: true,
							config: [],
							searchOnly: true,
							filtersConfig: []
						},
						expressions: {
							'props.config': () => this.getAddCashAccountsTableConfig(),
							'props.data': () => this.selectCashAccounts,
							'props.initialSelectedData': () => this.selectCashAccounts.filter((account) => account.Selected),
							'props.filters': () => {
								return { search: '' };
							},
						},
					},
				],
			},
		] as FormlyFieldConfig[];
	}

	private getAddCashAccountsTableConfig(): TableConfig<ICashAccountSelection> {
		return {
			defaultSortField: 'AccountNumber',
			defaultSortDirection: 'asc',
			stickyHeader: true,
			showRowSelect: true,
			selectionField: 'Selected',
			alwaysShowBulkActionToolbar: true,
			hideBulkActionButtons: true,
			columns: [
				{
					key: 'AccountNumber',
					header: 'Account Number',
					cellClass: 'table-select-account-display',
					template: (): TemplateRef<{ row: ICashAccountSelection }> => this.tblSelectAccountDisplay,
				},
				{
					key: 'Description',
					header: 'Description',
				},
			],
		};
	}
}
